import {
  Autocomplete,
  Avatar,
  createFilterOptions,
  Grid,
  IconButton,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  MenuList,
} from '@mui/material';
import sortBy from 'lodash.sortby';
import { observer } from 'mobx-react';
import React, { useRef, useState } from 'react';
import { localStore } from '../../local-store';
import { wait } from '../../utilFunctions/utils';
import { DomainSettings } from '../../v2/api/types';
import { LoadingCircle } from '../../v2/common/components/LoadingCircle';
import { Domains } from '../../v2/constants';
import {
  useAllowedDomainContext,
  useAllowedDomainsContext,
  useDomainContext,
  useDomainSetterContext,
  useEffectiveRoleViewAuthorizationServiceContext,
  useLocalStorageDomainServiceContext,
  useLocalStorageExecutorRoleServiceContext,
} from '../../v2/context/contexts';
import { WhiteTextField } from '../common-styled-components';
import { styled } from '../styled';
import PopUpMenu from '../utils/PopUpMenu';

const StyledIconButton = styled(IconButton)`
  padding: 5px !important;
` as typeof IconButton;

const StyledAvatar = styled(Avatar)`
  height: 20px;
  width: 20px;
  background: transparent;
  border-radius: 0;
` as typeof Avatar;

interface DomainMenuItemProps {
  domain: Domains;
  selectedDomain: Domains | null;
  logoURL: string | undefined;
  displayName: string;
  displayNameComponent: JSX.Element;
  menuItemProps: React.HTMLAttributes<HTMLLIElement>;
}

const DomainMenuItem = (props: DomainMenuItemProps) => {
  const {
    domain,
    selectedDomain,
    logoURL,
    displayName,
    displayNameComponent,
    menuItemProps,
  } = props;
  return (
    <MenuItem
      key={domain}
      selected={domain === selectedDomain}
      {...menuItemProps}
    >
      <ListItemAvatar>
        {logoURL ? (
          <StyledAvatar src={logoURL} />
        ) : (
          <StyledAvatar>{displayName}</StyledAvatar>
        )}
      </ListItemAvatar>
      <ListItemText
        primaryTypographyProps={{
          variant: 'body2',
          noWrap: true,
        }}
      >
        {displayNameComponent}
      </ListItemText>
    </MenuItem>
  );
};

export const Domain = observer(() => {
  const selectedDomain = useDomainContext();
  const domainSetter = useDomainSetterContext();
  const authorizationService =
    useEffectiveRoleViewAuthorizationServiceContext();
  const localStorageDomainService = useLocalStorageDomainServiceContext();
  const localStorageExecutorRoleService =
    useLocalStorageExecutorRoleServiceContext();
  const allowedDomains = useAllowedDomainsContext();
  const allowedDomain = useAllowedDomainContext();
  const isPanalytSuperAdmin = authorizationService.canManageAllDomains();
  const domainSearchRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const handleDomainLogoClick = async (
    _event: React.MouseEvent<HTMLElement>
  ) => {
    setOpen(true);
    await wait(100);
    if (domainSearchRef.current) {
      domainSearchRef.current.focus();
    }
  };
  const handleClose = () => {
    setOpen(false);
  };

  const onDomainSelect = (domain: Domains) => {
    setOpen(false);
    localStorageDomainService.set(domain);
    /**
     * When switching from role X to a different role (such as superadmin),
     * it's important to remove the executor role for X from local storage.
     * Otherwise, the app will retain the executor role for X after reloading,
     * which may not be the desired behavior. By removing the executor role for X,
     * the app will reset to the default view for the new role (e.g. the superadmin view).
     * */
    localStorageExecutorRoleService.clear();
    if (selectedDomain !== domain) {
      localStore.removeSavedStores();
    }
    domainSetter(domain);
    location.reload();
  };

  const logoURL = allowedDomains.length
    ? (allowedDomain.latestSettings?.settings?.logoURL ?? null)
    : null;
  const sortedSettings = sortBy(allowedDomains, (d) => d.domain).flatMap(
    (d) => {
      const settings = d.latestSettings?.settings;
      return settings ? [settings] : [];
    }
  );

  const getDomainMenuItem = (
    domainSettings: DomainSettings,
    menuItemProps: React.HTMLAttributes<HTMLLIElement> = {}
  ) => {
    const { domain, alias, name, logoURL } = domainSettings;
    const domainDisplayNameText = alias ? `${name} ( ${alias} )` : name;
    const domainDisplayNameNormalUser = <>{alias ?? name}</>;
    const domainDisplayNamePsa = alias ? (
      <>
        {' '}
        {name} <i>{alias}</i>{' '}
      </>
    ) : (
      <>{name}</>
    );
    return (
      <DomainMenuItem
        key={name}
        domain={domain}
        selectedDomain={selectedDomain}
        logoURL={logoURL}
        displayName={
          domainDisplayNameText && domainDisplayNameText.charAt(0).toUpperCase()
        }
        displayNameComponent={
          isPanalytSuperAdmin
            ? domainDisplayNamePsa
            : domainDisplayNameNormalUser
        }
        menuItemProps={menuItemProps}
      />
    );
  };

  const singleDomainView = (
    <PopUpMenu
      open={open}
      anchorEl={logoRef.current}
      onClose={handleClose}
      placement="bottom-end"
    >
      {sortedSettings.length > 0 ? (
        <MenuList>{sortedSettings.map((s) => getDomainMenuItem(s))}</MenuList>
      ) : (
        <LoadingCircle color="secondary" />
      )}
    </PopUpMenu>
  );

  return (
    <>
      <Grid container sx={{ width: '20vw', paddingRight: '8px' }}>
        <Grid item xs={11}>
          {/* only showing autocomplete for more than one domain  */}
          {sortedSettings.length > 1 ? (
            <Autocomplete
              disablePortal
              options={sortedSettings}
              getOptionLabel={(d) => {
                return d.domain;
              }}
              filterOptions={createFilterOptions({
                stringify: ({ domain, domainOfPartner, name, alias }) =>
                  [domain, domainOfPartner, name, alias].join('   '),
              })}
              renderOption={(props, option) => {
                return getDomainMenuItem(option, props);
              }}
              id="disable-close-on-select"
              renderInput={(params) => (
                <WhiteTextField
                  {...params}
                  variant="standard"
                  inputRef={domainSearchRef}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    '& .MuiAutocomplete-popupIndicator': { color: '#fff' },
                  }}
                />
              )}
              sx={[
                open
                  ? {
                      display: 'block',
                    }
                  : {
                      display: 'none',
                    },
              ]}
              onClose={handleClose}
              isOptionEqualToValue={(d1, d2) => d1.domain === d2.domain}
              onChange={(_e, value) => {
                if (value && value.domain) {
                  onDomainSelect(value.domain);
                }
              }}
              openOnFocus
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiPaper-root': {
                      background: '#00a599',
                      color: '#fff',
                    },
                  },
                },
              }}
            />
          ) : null}
        </Grid>
        <Grid item xs={1}>
          <StyledIconButton
            aria-haspopup="true"
            onClick={handleDomainLogoClick}
            color="inherit"
            className={'intercom_appbar_domain'}
            ref={logoRef}
          >
            {logoURL ? (
              <StyledAvatar src={logoURL} />
            ) : (
              <StyledAvatar>
                {selectedDomain && selectedDomain.charAt(0).toUpperCase()}
              </StyledAvatar>
            )}
          </StyledIconButton>
        </Grid>
      </Grid>
      {sortedSettings.length <= 1 && singleDomainView}
    </>
  );
});
